/**
 * Task stage pill — the read-only display of a task's stage.
 *
 * A router `Link` into the pipeline board when the viewer can read tasks (the
 * board scrolls the stage's column into view), a plain `Pill` otherwise.
 * Editing a stage is not this component's job: the Status row's
 * `InlineEditableFieldRow` opens the stage editor, exactly as a deal's stage
 * row does, so the pill stays a value, never a control of its own.
 *
 * Both variants render through the base `Pill`: the plain one IS a `<Pill>`;
 * the `Link` can't be a span, so it takes `pillClassName()` + `<PillDot>`
 * rather than a local copy of the styles. That keeps a stage the same size as
 * the assignee badge beside it — both are built from the shared chip scale.
 */

import type { MouseEvent } from 'react';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { usePermissions } from '@/hooks/usePermissions';
import { Pill, PillDot } from '@/lib/base/badges/pill';
import { pillClassName } from '@/lib/base/badges/pill-styles';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';
import { toKebabSlug } from '@/lib/crm/utils/entityTypeUtils';

import { TASK_ENTITY_TYPE } from './task.constants';

/** Hover affordance when the stage pill navigates to the pipeline. */
const STAGE_PILL_HOVER_CLASS =
  'hover:bg-[var(--color-bg-tertiary)] hover:text-primary transition-colors';

/** Long stage names truncate rather than stretch the row. */
const STAGE_LABEL_CLASS = 'truncate max-w-[140px]';

// The link navigates to the pipeline; it must not also trigger an outer
// click target (a clickable card, or the row's click-to-edit cell).
function stopPropagation(event: MouseEvent) {
  event.stopPropagation();
}

interface TaskStageLinkProps {
  orgId: number;
  stageEntityId: string | null | undefined;
}

/**
 * The stage's label and colour come from the org's task pipelines
 * (`usePipelines(orgId, 'ahoy_task')`). The outer component early-returns on
 * no stage so the pipelines query only runs for tasks that have one.
 */
export function TaskStageLink({ orgId, stageEntityId }: TaskStageLinkProps) {
  if (!stageEntityId) {
    return null;
  }
  return <TaskStageLinkResolved orgId={orgId} stageEntityId={stageEntityId} />;
}

function TaskStageLinkResolved({
  orgId,
  stageEntityId,
}: {
  orgId: number;
  stageEntityId: string;
}) {
  const { pipelines, isLoading } = usePipelines(orgId, TASK_ENTITY_TYPE);
  const { hasPermission } = usePermissions();
  const canOpenInPipeline = hasPermission(TASK_ENTITY_TYPE);
  const resolved = useMemo(() => {
    for (const pipeline of pipelines) {
      const stage = pipeline.stages.find(s => s.entity_id === stageEntityId);
      if (stage && pipeline.entity_id) {
        return { stage, pipelineId: pipeline.entity_id };
      }
    }
    return null;
  }, [pipelines, stageEntityId]);

  // The task has a stage, so the row must never read as empty. While the
  // pipelines load, show a placeholder pill; if they load but the stage no
  // longer resolves (removed from the pipeline), show a neutral fallback —
  // mirroring PipelineStageValueBadge's loading / unknown-stage states.
  if (!resolved) {
    if (isLoading) {
      return <Pill className='animate-pulse'>…</Pill>;
    }
    return <Pill>Unknown stage</Pill>;
  }

  const { stage, pipelineId } = resolved;

  // The stage's configured colour is data, so it rides on the Pill's dot
  // rather than a class. An unset colour renders no dot.
  const dotColor = stage.properties.color || undefined;
  const label = <span className={STAGE_LABEL_CLASS}>{stage.label}</span>;

  if (!canOpenInPipeline) {
    return <Pill dot={dotColor ?? false}>{label}</Pill>;
  }

  // Match the per-org URL shape used by SideNavigation + PipelinePage:
  //   /<orgId>/pipeline/<entitySlug>/<pipelineId>?stage=<stageId>
  const to = `/${orgId}/pipeline/${toKebabSlug(TASK_ENTITY_TYPE)}/${pipelineId}?stage=${stage.entity_id}`;

  return (
    <Link
      to={to}
      title={`Open ${stage.label} in the pipeline`}
      onClick={stopPropagation}
      className={pillClassName({
        hasDot: Boolean(dotColor),
        className: STAGE_PILL_HOVER_CLASS,
      })}
    >
      {dotColor && <PillDot dotColor={dotColor} />}
      {label}
    </Link>
  );
}
